import api, {
  getBaseUrlForRegion,
  RegionCode,
} from "@/lib/axiosConfig.ts";
import {
  RegionInstancesFetchError,
  SessionExpiredError,
} from "@/lib/instance-errors";
import { tabMenuItems } from "@/lib/types";
import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";

export { SessionExpiredError } from "@/lib/instance-errors";

async function getAllinstanceForAUser(userId: string | undefined) {
  const { data } = await api.get("back/api/user/instance/" + userId);
  return data;
}

export const useGetAllinstanceForAUser = (userId: string | undefined) => {
  return useQuery({
    queryKey: ["instance", userId],
    queryFn: () => getAllinstanceForAUser(userId),
    enabled: !!userId,
  });
};

/**
 * Résout le user.id dans la DB d'une région à partir de son token de session,
 * puis récupère ses instances. Les IDs user diffèrent entre régions (même
 * email = rows distinctes), on ne peut pas réutiliser l'id d'une région pour
 * en interroger une autre.
 */
export async function getMyInstancesForRegion(
  token: string,
  regionCode: RegionCode,
  signal?: AbortSignal,
): Promise<UserInstance[]> {
  const baseUrl = getBaseUrlForRegion(regionCode);
  let users: Array<{ id: string }>;

  try {
    const response = await axios.get<Array<{ id: string }>>(
      `${baseUrl}back/api/user/${token}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
    );
    users = response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new SessionExpiredError(regionCode);
      }
      throw new RegionInstancesFetchError(
        regionCode,
        error.response?.status,
        error.message,
      );
    }
    throw error;
  }

  const userId = users?.[0]?.id;
  if (!userId) return [];

  try {
    const { data } = await axios.get<UserInstance[]>(
      `${baseUrl}back/api/user/instance/${userId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      },
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        throw new SessionExpiredError(regionCode);
      }
      throw new RegionInstancesFetchError(
        regionCode,
        error.response?.status,
        error.message,
      );
    }
    throw error;
  }
}

/**
 * Shape d'une entrée retournée par back/api/user/instance/:userId
 */
export interface UserInstance {
  role: string;
  instance: {
    id: string;
    name: string;
  };
  /** Code région ajouté côté client lors de la fusion des listes */
  regionCode?: RegionCode;
}

async function getInstance(instanceName: string | undefined) {
  const { data } = await api.get("back/api/instance/" + instanceName);
  return data;
}

/**
 * Récupère un token de prévisualisation signé pour l'instance courante, permettant
 * d'ouvrir le site public protégé par basic auth sans saisir d'identifiants.
 * L'instance ciblée est déterminée côté API via le header `Instance` (auto-attaché).
 */
export async function getPreviewToken(): Promise<{
  token: string;
  frontUrl: string;
}> {
  const { data } = await api.get("back/api/instance/preview-token");
  return data;
}

/** URL de la page « Tutoriels » du site vitrine, mot de passe inclus (fabriquée par l'API). */
export async function getTutorialsLink(): Promise<{ url: string }> {
  const { data } = await api.get("back/api/instance/tutorials-link");
  return data;
}

export const useGetInstance = (instanceName: string | undefined) => {
  return useQuery({
    queryKey: ["instance", instanceName],
    queryFn: () => getInstance(instanceName),
    placeholderData: keepPreviousData,
    enabled: !!instanceName,
  });
};

export type InstanceWatermarkConfig = {
  watermarkEnabled?: boolean;
  watermarkText?: string | null;
  watermarkPosition?: string | null;
  watermarkFontFamily?: string | null;
  watermarkFontWeight?: string | null;
  watermarkFontSize?: number | null;
  watermarkRotate90?: boolean | null;
  watermarkOpacity?: number | null;
};

async function getCurrentInstance() {
  const { data } = await api.get("back/api/instance/current");
  return data;
}

export const useGetCurrentInstance = () => {
  return useQuery({
    queryKey: ["instance", "current"],
    queryFn: getCurrentInstance,
  });
};

async function updateCurrentInstance(payload: InstanceWatermarkConfig) {
  const { data } = await api.patch("back/api/instance/current", payload);
  return data;
}

export const useUpdateCurrentInstance = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCurrentInstance,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instance", "current"] });
    },
  });
};

export type CountData = {
  [key in (typeof tabMenuItems)[number]]: number;
};

async function countData(): Promise<CountData> {
  const { data } = await api.get("back/api/instance/count-data");
  return data;
}

export const useCountData = () => {
  return useQuery({
    queryKey: ["instance", "count-data"],
    queryFn: () => countData(),
  });
};
